import ApiClient from "src/utils/ApiClient";
import reportErrorToNewRelic from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/reportErrorToNewRelic";
import { DASHBOARD_ROUTES } from "src/Pages/Company/Dashboard/services/api/apiRoutes";
import {
  CreateMenuTemplatePayload,
  CreateMenuVariantPayload,
  CursorPaginatedResponse,
  CursorPaginationParams,
  DashboardModuleCompanyRedirectsWidgetDTO,
  DashboardModuleCompanyRedirectsWidgetNewIconFromWebsiteDTO,
  DashboardModuleCompanyRedirectsWidgetPayload,
  DashboardModuleConfigurationDTO,
  DashboardModuleConfigurationPayload,
  GroupDTO,
  GroupType,
  LogoDTO,
  MenuTemplateInfoDTO,
  MenuTemplateResponseDTO,
  MenuVariantInfoDTO,
  MenuVariantResponseDTO,
  ModulesConfigurationDTO,
  PagePaginatedResponse,
  PagePaginationParams,
  ReadConfigurationResponseDTO,
  UpdateConfigurationPayload,
  UpdateDefaultLogoPayload,
  UpdateMenuTemplatePayload,
  UpdateMenuVariantPayload,
  UpdateModulesConfigurationPayload,
} from "src/Pages/Company/Dashboard/services/api/api.models";

export const DashboardApiClient = new ApiClient({
  baseURL: "/api/mybenefit-dashboard/omb",
  headers: {},
}).client;

DashboardApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    reportErrorToNewRelic(error, "Dashboard API error");

    return Promise.reject(error);
  }
);

export const fetchDashboardModuleConfiguration = async (
  companyId: string
): Promise<DashboardModuleConfigurationDTO> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MODULE_CONFIGURATION(companyId)
  );

  return data;
};

export const fetchDashboardModuleCompanyRedirectsWidget = async (
  companyId: string
): Promise<DashboardModuleCompanyRedirectsWidgetDTO> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MODULE_COMPANY_REDIRECTS_WIDGET(companyId)
  );

  return data;
};

export const fetchDashboardModuleCompanyRedirectsWidgetNewIconFromWebsite =
  async (
    companyId: string,
    payload: { websiteUrl: string }
  ): Promise<DashboardModuleCompanyRedirectsWidgetNewIconFromWebsiteDTO> => {
    const { data } = await DashboardApiClient.post(
      DASHBOARD_ROUTES.POST.DASHBOARD_MODULE_COMPANY_REDIRECTS_NEW_ICON_FROM_WEBSITE(
        companyId
      ),
      payload
    );

    return data;
  };

export const updateDashboardModuleConfiguration = async ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: DashboardModuleConfigurationPayload;
}) => {
  const { data } = await DashboardApiClient.put(
    DASHBOARD_ROUTES.PUT.DASHBOARD_MODULE_CONFIGURATION(companyId),
    payload
  );

  return data;
};

export const uploadDashboardModuleCompanyRedirectsWidgetIcon = async (
  companyId: string,
  file: File
): Promise<DashboardModuleCompanyRedirectsWidgetNewIconFromWebsiteDTO> => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await DashboardApiClient.post(
    DASHBOARD_ROUTES.POST.DASHBOARD_MODULE_COMPANY_REDIRECTS_NEW_ICON(
      companyId
    ),
    formData
  );

  return data;
};

export const updateDashboardModuleCompanyRedirects = async ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: DashboardModuleCompanyRedirectsWidgetPayload;
}) => {
  const { data } = await DashboardApiClient.put(
    DASHBOARD_ROUTES.PUT.DASHBOARD_MODULE_COMPANY_REDIRECTS(companyId),
    payload
  );

  return data;
};

// Groups

export const fetchDashboardGroups = async (
  companyId: string,
  groupType: GroupType,
  params?: CursorPaginationParams
): Promise<CursorPaginatedResponse<GroupDTO>> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MODULE_GROUPS(companyId, groupType),
    { params }
  );

  return data;
};

// Logo

export const fetchDefaultLogo = async (): Promise<LogoDTO> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MODULE_DEFAULT_LOGO
  );

  return data;
};

export const uploadLogo = async (file: File): Promise<LogoDTO> => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await DashboardApiClient.post(
    DASHBOARD_ROUTES.POST.DASHBOARD_MODULE_CREATE_LOGO,
    formData
  );

  return data;
};

export const updateDefaultLogo = async (
  payload: UpdateDefaultLogoPayload
): Promise<void> => {
  await DashboardApiClient.put(
    DASHBOARD_ROUTES.PUT.DASHBOARD_MODULE_DEFAULT_LOGO,
    payload
  );
};

// Menu Templates

export const fetchMenuTemplates = async (
  params?: CursorPaginationParams
): Promise<CursorPaginatedResponse<MenuTemplateInfoDTO>> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MENU_TEMPLATES,
    { params }
  );

  return data;
};

export const fetchMenuTemplatesPaged = async (
  params?: PagePaginationParams
): Promise<PagePaginatedResponse<MenuTemplateInfoDTO>> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MENU_TEMPLATES_PAGED,
    { params }
  );

  return data;
};

export const fetchMenuTemplate = async (
  menuId: string
): Promise<MenuTemplateResponseDTO> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MENU_TEMPLATE(menuId)
  );

  return data;
};

export const createMenuTemplate = async (
  payload: CreateMenuTemplatePayload
): Promise<void> => {
  await DashboardApiClient.post(
    DASHBOARD_ROUTES.POST.DASHBOARD_MENU_TEMPLATE,
    payload
  );
};

export const updateMenuTemplate = async ({
  templateId,
  payload,
}: {
  templateId: string;
  payload: UpdateMenuTemplatePayload;
}): Promise<void> => {
  await DashboardApiClient.put(
    DASHBOARD_ROUTES.PUT.DASHBOARD_MENU_TEMPLATE(templateId),
    payload
  );
};

export const deleteMenuTemplate = async (
  templateId: string
): Promise<void> => {
  await DashboardApiClient.delete(
    DASHBOARD_ROUTES.DELETE.DASHBOARD_MENU_TEMPLATE(templateId)
  );
};

// Menu Variants

export const fetchMenuVariants = async (
  companyId: string,
  params?: CursorPaginationParams
): Promise<CursorPaginatedResponse<MenuVariantInfoDTO>> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MENU_VARIANTS(companyId),
    { params }
  );

  return data;
};

export const fetchMenuVariant = async (
  companyId: string,
  menuId: string
): Promise<MenuVariantResponseDTO> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MENU_VARIANT(companyId, menuId)
  );

  return data;
};

export const createMenuVariant = async ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: CreateMenuVariantPayload;
}): Promise<void> => {
  await DashboardApiClient.post(
    DASHBOARD_ROUTES.POST.DASHBOARD_MENU_VARIANT(companyId),
    payload
  );
};

export const updateMenuVariant = async ({
  companyId,
  menuId,
  payload,
}: {
  companyId: string;
  menuId: string;
  payload: UpdateMenuVariantPayload;
}): Promise<void> => {
  await DashboardApiClient.put(
    DASHBOARD_ROUTES.PUT.DASHBOARD_MENU_VARIANT(companyId, menuId),
    payload
  );
};

export const deleteMenuVariant = async ({
  companyId,
  menuId,
}: {
  companyId: string;
  menuId: string;
}): Promise<void> => {
  await DashboardApiClient.delete(
    DASHBOARD_ROUTES.DELETE.DASHBOARD_MENU_VARIANT(companyId, menuId)
  );
};

// Company Configuration

export const fetchCompanyConfiguration = async (
  companyId: string
): Promise<ReadConfigurationResponseDTO> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_COMPANY_CONFIGURATION(companyId)
  );

  return data;
};

export const updateCompanyConfiguration = async ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: UpdateConfigurationPayload;
}): Promise<ReadConfigurationResponseDTO> => {
  const { data } = await DashboardApiClient.put(
    DASHBOARD_ROUTES.PUT.DASHBOARD_COMPANY_CONFIGURATION(companyId),
    payload
  );

  return data;
};

// Modules Configuration

export const fetchModulesConfiguration = async (
  companyId: string
): Promise<ModulesConfigurationDTO> => {
  const { data } = await DashboardApiClient.get(
    DASHBOARD_ROUTES.GET.DASHBOARD_MODULES_CONFIGURATION(companyId)
  );

  return data;
};

export const updateModulesConfiguration = async ({
  companyId,
  payload,
}: {
  companyId: string;
  payload: UpdateModulesConfigurationPayload;
}): Promise<void> => {
  await DashboardApiClient.put(
    DASHBOARD_ROUTES.PUT.DASHBOARD_MODULES_CONFIGURATION(companyId),
    payload
  );
};
